import React, { Key } from 'react';
import { useAsyncList } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';
import { LoadingState } from '@react-types/shared';
import cn from 'classnames';

import { signal } from '@nw/shared/config';

import { useQueryClientStatesHook } from '../../hooks/useQueryClientStatesHook';
import { useStateAndDistricts } from '../../hooks/useStateAndDistricts';
import {
  getFilteredStates,
  getStateIdFromStateName,
  getStateNameFromStateId
} from '../../utils/ComboBoxUtils';

import { ComboBox } from '../ComboboxWithInitialValue';
import {
  AsyncListLoadFunction,
  OptionsType,
  OverlayOptions,
  State
} from '../types';

export interface StatesComboBoxProps {
  countryId?: string;
  label: string;
  options?: Array<OptionsType>;
  onSelectionChange?: (e: Key) => void;
  selectedKey?: string;
  placeholder?: string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onOpenChange?: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error?: string;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  initialStateValue?: string;
  getDisplayValueFromSelectedState?: (T: any) => string;
  helperText?: string;
  hintClassName?: string;
  enableDataMask?: boolean;
  mobilePlaceholder?: string;
}

export const StatesComboBox = (props: StatesComboBoxProps) => {
  const {
    countryId,
    onOpenChange,
    onInputChange,
    onSelectionChange,
    selectedKey,
    inputValue,
    inputFieldStyles = '',
    buttonStyles = '',
    placeholder = '',
    error,
    initialStateValue,
    getDisplayValueFromSelectedState,
    helperText,
    hintClassName,
    enableDataMask,
    mobilePlaceholder,
    ...others
  } = props;

  const { states } = useStateAndDistricts();

  const onSelectionChangeFunc = (key) => {
    onSelectionChange && onSelectionChange(key);
  };

  const onOpenChangeFunc = (key: any) => {
    onOpenChange && onOpenChange(key);
  };

  const onInputChangeFunc = (value) => {
    onInputChange && onInputChange(value);
  };

  const statesListPromise = useQueryClientStatesHook(signal, countryId);

  const loadItems: AsyncListLoadFunction<
    { state_id: string; state_name: any },
    any
  > = async ({ signal, cursor, filterText, selectedKeys, items }) => {
    const json = await statesListPromise;

    const selectedKey = (selectedKeys as Set<React.Key>).values().next().value;

    states.current = json.states; //FIXME: use updateStates from useStateAndDistricts

    const filteredStates = Boolean(initialStateValue && !selectedKey)
      ? json.states
      : getFilteredStates(filterText, json.states);

    return {
      items: filteredStates,
      cursor: null,
      filterText: filterText
    };
  };

  const list = useAsyncList({
    load: loadItems,
    initialFilterText: initialStateValue ?? '',
    initialSelectedKeys: [],
    getKey: (item: State) => item.state_id
  });

  const getInputTextFromValue = (value, list) => {
    return getStateNameFromStateId(value, list);
  };

  const getValueFromInputText = (text, list) => {
    return getStateIdFromStateName(text, list);
  };

  return (
    <ComboBox
      {...others}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChangeFunc}
      onInputChange={onInputChangeFunc}
      onOpenChange={onOpenChangeFunc}
      inputFieldStyles={cn('w-full', inputFieldStyles)}
      buttonStyles={buttonStyles}
      getListItemKey={(item) => item.state_id}
      placeholder={placeholder}
      getListItemTextValue={(item) => item.state_name}
      renderListItem={({ item }) => (
        <p key={item.state_id}>{item.state_name}</p>
      )}
      error={error}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      initialValue={initialStateValue}
      list={list}
      getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
      helperText={helperText}
      hintClassName={hintClassName}
      enableDataMask={enableDataMask}
      mobilePlaceholder={mobilePlaceholder}
    />
  );
};
